/**
 * Email Verification Banner Component
 * 
 * Shows a reminder banner for DAC users who haven't verified their email.
 * Includes resend verification email functionality.
 */

import { useState } from 'react';
import { AlertTriangle, Mail, X, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailVerificationBanner({ user, onDismiss }) {
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Don't show if user is verified, not a DAC, or banner dismissed
  if (!user || user.role !== 'DAC' || user.email_verified || dismissed) {
    return null;
  }

  const handleResendEmail = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/send-verification-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setEmailSent(true);
        toast.success('Verification email sent!', {
          description: 'Please check your inbox and spam folder.'
        });
      } else {
        toast.error('Failed to send email', {
          description: data.detail || 'Please try again later.'
        });
      }
    } catch (error) {
      toast.error('Error sending email', {
        description: 'Please check your connection and try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 p-2 rounded-full">
              {emailSent ? (
                <CheckCircle className="w-5 h-5 text-amber-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-amber-800">
                {emailSent ? 'Verification email sent!' : 'Please verify your email address'}
              </p>
              <p className="text-xs text-amber-600">
                {emailSent 
                  ? 'Check your inbox and click the verification link.'
                  : 'Verify your email to ensure you receive important order updates.'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {!emailSent && (
              <Button
                size="sm"
                variant="outline"
                className="bg-white border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={handleResendEmail}
                disabled={loading}
                data-testid="resend-verification-btn"
              >
                <Mail className="w-4 h-4 mr-1" />
                {loading ? 'Sending...' : 'Send Verification Email'}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="text-amber-600 hover:text-amber-800 hover:bg-amber-100"
              onClick={handleDismiss}
              data-testid="dismiss-verification-banner"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
